import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { loadConfig } from "../helpers/config";
import LoadingSpinner from "../components/LoadingSpinner";
import ConvertDate from "../helpers/DateConverter"; // Import the date converter function
import { findNextDueTool, findNextDueCategoryForTool } from "../helpers/dueChecks";

function Check() {
  const pdfRef = useRef();
  const { id } = useParams();
  const navigate = useNavigate();
  const [CheckObject, setCheckObject] = useState({});
  const [apiUrl, setApiUrl] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [category, setCategory] = useState(null);
  const [tools, setTools] = useState([]);
  const [checks, setChecks] = useState([]);
  const [groupCategories, setGroupCategories] = useState([]);

  useEffect(() => {
    async function fetchConfig() {
      let url = await loadConfig();
      setApiUrl(url.APIURL);
      setLoadingConfig(false);
    }
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!loadingConfig) {
      axios.get(`${apiUrl}/check/${id}`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      }).then((response) => {
        setCheckObject(response.data);
        // Fetch all check categories of this tool's group (for the
        // "andere Prüfkategorie" button)
        const groupId = response.data.Tool?.ToolsGroup?.id ?? response.data.Tool?.ToolsGroupId;
        if (groupId) {
          axios.get(`${apiUrl}/categories/toolsgroup/${groupId}`, {
            headers: { accessToken: localStorage.getItem("accessToken") }
          }).then(res => setGroupCategories(res.data)).catch(() => setGroupCategories([]));
        }
        // Fetch category info if available
        if (response.data.categoryId) {
          axios.get(`${apiUrl}/categories/${response.data.categoryId}`, {
            headers: { accessToken: localStorage.getItem("accessToken") }
          }).then(catRes => {
            setCategory(catRes.data);
          }).catch(() => setCategory(null));
        } else {
          setCategory(null);
        }
      });
    }
  }, [loadingConfig, apiUrl, id]);

  // Load tools and checks to determine the next due check of the same
  // tools group and category (for the "Weitere Prüfung starten" button).
  useEffect(() => {
    if (!loadingConfig) {
      const headers = { accessToken: localStorage.getItem("accessToken") };
      axios.get(`${apiUrl}/tools`, { headers })
        .then((res) => setTools(res.data))
        .catch(() => setTools([]));
      axios.get(`${apiUrl}/checks`, { headers })
        .then((res) => setChecks(res.data))
        .catch(() => setChecks([]));
    }
  }, [loadingConfig, apiUrl]);

  if (loadingConfig) {
    return <LoadingSpinner text="Lade Konfiguration..." />;
  }

  if(!Object.keys(CheckObject).length){
    return null;
  }

  // Prevent access to invalid checks
  if (CheckObject.isValid === false) {
    return (
      <div className="container">
        <div className="alert alert-danger mt-4">
          <h4>Ungültige Prüfung</h4>
          <p>Diese Prüfung wurde als ungültig markiert und kann nicht mehr eingesehen werden.</p>
        </div>
      </div>
    );
  }

  let customChecks = CheckObject.customChecks;
  if (typeof customChecks === "string") {
    try {
      customChecks = JSON.parse(customChecks);
      // If customChecks is still a string (double-stringified), parse again
      if (typeof customChecks === "string") {
        customChecks = JSON.parse(customChecks);
      }
    } catch {
      customChecks = {};
    }
  }
  customChecks = customChecks && typeof customChecks === "object" ? customChecks : {};

  const toolsGroupId = CheckObject.Tool?.ToolsGroup?.id ?? CheckObject.Tool?.ToolsGroupId;
  const currentToolId = CheckObject.Tool?.id ?? CheckObject.ToolId;
  const groupname = CheckObject.Tool?.ToolsGroup?.groupname;
  const nextDue = (category && toolsGroupId)
    ? findNextDueTool({ tools, checks, toolsGroupId, category, excludeToolId: currentToolId })
    : null;

  const goToGroupTools = () => {
    navigate(groupname ? `/Tools?group=${encodeURIComponent(groupname)}` : "/Tools");
  };

  const startNextDueCheck = () => {
    if (!nextDue) return;
    navigate(`/Checks/Add/${nextDue.tool.id}`, { state: { categoryId: category.id } });
  };

  // Another overdue check category for the same tool, if one exists
  const nextDueCategory = (CheckObject.Tool && groupCategories.length > 0)
    ? findNextDueCategoryForTool({
        tool: CheckObject.Tool,
        checks,
        categories: groupCategories,
        excludeCategoryId: category ? category.id : CheckObject.categoryId
      })
    : null;

  const startOtherCategoryCheck = () => {
    if (!nextDueCategory) return;
    navigate(`/Checks/Add/${currentToolId}`, { state: { categoryId: nextDueCategory.category.id } });
  };

  return (
    <div className="container">
      <div className="card" ref={pdfRef}>
        <div className="card-header">
          Prüfung {CheckObject.id} für {CheckObject.Tool.ToolsGroup.groupname} {CheckObject.Tool.serialnumber}
        </div>
        <div className="card-body">
          <div className="check-details-grid">
            <div className="detail-item">
              <strong>Prüfer:</strong>
              <span>{CheckObject.inspector || "Unbekannt"}</span>
            </div>
            {category && (
              <div className="detail-item">
                <strong>Prüfkategorie:</strong>
                <span>{category.name} {category.interval ? `(${category.interval} ${category.unit})` : ""}</span>
              </div>
            )}
            <div className="detail-item">
              <strong>Prüfdatum:</strong>
              <span>{CheckObject.checkdate ? ConvertDate(CheckObject.checkdate) : "-"}</span>
            </div>
            {CheckObject.notes && (
              <div className="detail-item detail-item-full">
                <strong>Notizen:</strong>
                <p>{CheckObject.notes}</p>
              </div>
            )}
          </div>

          {/* Render custom check results if present */}
          {customChecks && Object.keys(customChecks).length > 0 && (
            <div className="custom-checks-section">
              <h4>Individuelle Prüfwerte:</h4>
              <ul className="custom-checks-list">
                {Object.entries(customChecks).map(([key, value]) => (
                  <li key={key}><strong>{key}:</strong> {String(value)}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="check-result-container">
            <h4>Prüfergebnis</h4>
            <div className={`check-result ${CheckObject.checkresult === "OK" ? "check-result-ok" : "check-result-failed"}`}>
              {CheckObject.checkresult === "OK" ? "In Ordnung" : "Durchgefallen"}
            </div>
          </div>

          <div className="check-actions" style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "10px", marginTop: "20px" }}>
            {CheckObject.pdfPath && (
              <a
                className="download-pdf-btn"
                href={`${apiUrl}${CheckObject.pdfPath}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ justifyContent: "center", marginTop: 0 }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                  <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                </svg>
                Prüfprotokoll als PDF herunterladen
              </a>
            )}

            <button type="button" className="btn btn-secondary" onClick={goToGroupTools}>
              Alle Geräte der Gerätegruppe{groupname ? ` „${groupname}"` : ""} anzeigen
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={startNextDueCheck}
              disabled={!nextDue}
              title={
                nextDue
                  ? `Nächstes fälliges Gerät: ${nextDue.tool.serialnumber}${category ? ` – Prüfkategorie „${category.name}"` : ""}`
                  : "In dieser Gerätegruppe und Prüfkategorie ist derzeit keine weitere Prüfung fällig"
              }
            >
              {nextDue
                ? `Nächste fällige Prüfung starten – Gerät ${nextDue.tool.serialnumber}${category ? ` (${category.name})` : ""}`
                : "Keine weitere fällige Prüfung in dieser Prüfkategorie"}
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={startOtherCategoryCheck}
              disabled={!nextDueCategory}
              title={
                nextDueCategory
                  ? `Überfällige Prüfkategorie für ${CheckObject.Tool?.serialnumber}: „${nextDueCategory.category.name}"`
                  : "Für dieses Gerät ist keine weitere Prüfkategorie überfällig"
              }
            >
              {nextDueCategory
                ? `Überfällige Prüfung dieses Geräts starten – Prüfkategorie „${nextDueCategory.category.name}"`
                : "Keine weitere überfällige Prüfkategorie für dieses Gerät"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
export default Check;
